import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";
import clsx from "clsx";

/**
 * A labelled input.
 *
 * The label is always present and always visible — a placeholder standing in for a
 * label disappears the moment someone types, which in a form full of dates and
 * amounts is how a Betriebskostenabrechnung ends up with a figure in the wrong
 * field. Placeholders here carry the *format* (`TT.MM.JJJJ`), nothing else.
 */

export interface FeldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label: string;
  /** Shown under the field; replaced by `fehler` when there is one. */
  hinweis?: string;
  fehler?: string;
  /** Right-aligned tabular figures, for amounts and areas. */
  numerisch?: boolean;
  nachspann?: ReactNode;
}

export const Feld = forwardRef<HTMLInputElement, FeldProps>(function Feld(
  { label, hinweis, fehler, numerisch = false, nachspann, className, id, ...rest },
  ref,
) {
  const generated = useId();
  const feldId = id ?? generated;
  const beschreibungId = fehler ? `${feldId}-fehler` : hinweis ? `${feldId}-hinweis` : undefined;

  return (
    <div className="flex flex-col gap-[4px]">
      <label
        htmlFor={feldId}
        className="text-[length:var(--text-2xs)]"
        style={{
          color: "var(--text-caps)",
          fontWeight: "var(--weight-medium)",
          textTransform: "var(--label-transform)" as never,
          letterSpacing: "var(--tracking-caps)",
        }}
      >
        {label}
      </label>

      <div className="relative flex items-center">
        <input
          ref={ref}
          id={feldId}
          aria-invalid={fehler ? true : undefined}
          aria-describedby={beschreibungId}
          className={clsx(
            "w-full h-[30px] px-[8px] border",
            "text-[length:var(--text-sm)]",
            "rounded-[var(--radius-2)]",
            "outline-none",
            numerisch && "num",
            className,
          )}
          style={{
            background: rest.readOnly ? "var(--surface-field-ro)" : "var(--surface-field)",
            borderColor: fehler ? "var(--status-bad-bar)" : "var(--line-field)",
            color: "var(--text-primary)",
          }}
          {...rest}
        />
        {nachspann ? (
          <span
            className="absolute right-[8px] text-[length:var(--text-xs)] pointer-events-none"
            style={{ color: "var(--text-muted)" }}
          >
            {nachspann}
          </span>
        ) : null}
      </div>

      {fehler ? (
        <p id={beschreibungId} className="text-[length:var(--text-2xs)]" style={{ color: "var(--status-bad-tx)" }}>
          {fehler}
        </p>
      ) : hinweis ? (
        <p id={beschreibungId} className="text-[length:var(--text-2xs)]" style={{ color: "var(--text-muted)" }}>
          {hinweis}
        </p>
      ) : null}
    </div>
  );
});
